import { useEffect, useState } from "react";
import { Navigate, Routes, Route, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import { onAuthStateChanged } from "firebase/auth";

import Homepage from "./pages/homepage";
import About from "./pages/about";
import Projects from "./pages/projects";
import Articles from "./pages/articles";
import Contact from "./pages/contact";
import Notfound from "./pages/404";

import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import Books from "./pages/Books";
import Events from "./pages/Events";
import EventTicketSuccess from "../src/pages/EventTicketSuccess";
import TicketScanner from "./pages/TicketScanner";
import KeyNotes from "./pages/KeyNotes";
import BookSuccess from "../src/pages/BookSuccess";
import BookingSuccess from "../src/pages/BookingSuccess";
import BookingReschedule from "./pages/BookingReschedule";
import FaqChatbot from "./components/common/faqChatbot";
import RouteSEO from "./components/common/RouteSEO";
import BlogList from "./pages/BlogList";
import ReadBlog from "./pages/ReadBlog";
import { auth } from "./firebase";


import { TRACKING_ID } from "./data/tracking";
import AnalyticsConsent, { getAnalyticsConsent } from "./components/common/AnalyticsConsent";
import "./App.css";

const PRIVATE_ANALYTICS_PATHS = [
	"/admin",
	"/booking/success",
	"/booking/reschedule",
	"/articles/success",
	"/events/success",
];

function isPublicAnalyticsPath(pathname) {
	return !PRIVATE_ANALYTICS_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

function AnalyticsPageViews({ enabled }) {
	const location = useLocation();

	useEffect(() => {
		if (!enabled || !isPublicAnalyticsPath(location.pathname)) return;
		// Deliberately omit location.search: it can contain booking and payment
		// identifiers that must not be sent to an analytics provider.
		ReactGA.send({
			hitType: "pageview",
			page: location.pathname,
			title: document.title,
		});
	}, [enabled, location.pathname]);

	return null;
}

function ProtectedAdminRoute({ children }) {
	const [user, setUser] = useState(null);
	const [authLoading, setAuthLoading] = useState(true);

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
			setUser(currentUser);
			setAuthLoading(false);
		});

		return unsubscribe;
	}, []);

	if (authLoading) return null;
	if (!user) return <Navigate to="/admin" replace />;

	return children;
}

function App() {
	const [analyticsConsent, setAnalyticsConsent] = useState(getAnalyticsConsent);
	const [analyticsReady, setAnalyticsReady] = useState(false);

	useEffect(() => {
		if (!TRACKING_ID || analyticsConsent !== "granted" || analyticsReady) return;
		// Basic consent mode: the GA tag is not loaded and no data is sent until
		// the visitor explicitly accepts analytics.
		ReactGA.initialize(TRACKING_ID, { gtagOptions: { send_page_view: false } });
		setAnalyticsReady(true);
	}, [analyticsConsent, analyticsReady]);

	const saveAnalyticsConsent = (choice) => {
		try {
			window.localStorage.setItem("jabuzwane-analytics-consent", choice);
		} catch {
			// A blocked localStorage setting should not prevent visitors using the site.
		}
		setAnalyticsConsent(choice);
	};

	return (
		<div className="App">
			<AnalyticsPageViews enabled={analyticsReady} />
			<RouteSEO />
			<Routes>
				<Route path="/" element={<Homepage />} />
				<Route path="/about" element={<About />} />
				<Route path="/projects" element={<Projects />} />
				<Route path="/articles" element={<Articles />} />
				<Route path="/contact" element={<Contact />} />
				<Route path="/media" element={<Navigate to="/projects" replace />} />
				<Route path="/postedarticles" element={<Navigate to="/projects" replace />} />
				<Route path="/postedarticles/*" element={<Navigate to="/projects" replace />} />
				<Route path="/article/:slug" element={<Navigate to="/projects" replace />} />
				<Route path="/admin" element={<AdminLogin />} />
				<Route
					path="/admin/dashboard"
					element={
						<ProtectedAdminRoute>
							<AdminDashboard />
						</ProtectedAdminRoute>
					}
				/>
				<Route
					path="/admin/books"
					element={
						<ProtectedAdminRoute>
							<Books />
						</ProtectedAdminRoute>
					}
				/>
				<Route
					path="/admin/ticket-scanner"
					element={
						<ProtectedAdminRoute>
							<TicketScanner />
						</ProtectedAdminRoute>
					}
				/>
				<Route path="/events" element={<Events />} />
				<Route path="/events/success" element={<EventTicketSuccess />} />
				<Route path="/keynotes" element={<KeyNotes />} />
				<Route path="/articles/success" element={<BookSuccess />} />
				<Route path="/booking/success" element={<BookingSuccess />} />
				<Route path="/booking/reschedule" element={<BookingReschedule />} />
				<Route path="/blog" element={<BlogList />} />
				<Route path="/events/success" element={<EventTicketSuccess />} />
				<Route path="/blog/:id" element={<ReadBlog />} />

				{/* Legacy URLs kept working so old bookmarks/shared links don't break */}
				<Route path="/Jabu-Zwane-Blogs" element={<Navigate to="/blog" replace />} />
				<Route path="/Jabu-Zwane-Blogs/*" element={<Navigate to="/blog" replace />} />
				<Route path="/Contacts" element={<Navigate to="/contact" replace />} />
				<Route path="/Contacts/*" element={<Navigate to="/contact" replace />} />

				<Route path="*" element={<Notfound />} />
			</Routes>
			<FaqChatbot />
			{TRACKING_ID && <AnalyticsConsent consent={analyticsConsent} onChoose={saveAnalyticsConsent} />}
		</div>
	);
}

export default App;
